/**
 * BashToolRenderer: renders shell command output with head+tail truncation,
 * streaming support, and error state display.
 */

import chalk from 'chalk';
import type { ToolRenderer, ToolRenderContext, ToolCallDisplay, ToolResultDisplay } from './types.js';
import type { BashDetails, BashStreamUpdate } from '@animus-labs/cortex';
import { collapseContent } from './collapsible-content.js';
import { StreamingBuffer } from './streaming-buffer.js';
import { registerRenderer } from './registry.js';

const ERROR_HEAD = 4;
const ERROR_TAIL = 4;
const STREAMING_WINDOW = 3;

function extractTextContent(result: unknown): string {
  if (typeof result === 'string') return result;
  if (result && typeof result === 'object' && 'content' in (result as Record<string, unknown>)) {
    const content = (result as Record<string, unknown>)['content'];
    if (Array.isArray(content)) {
      return content
        .filter((c: unknown) => c && typeof c === 'object' && (c as Record<string, unknown>)['type'] === 'text')
        .map((c: unknown) => (c as Record<string, string>)['text'])
        .join('\n');
    }
  }
  return String(result ?? '');
}

function stripTrailingExitCode(text: string, exitCode: number | null | undefined): string {
  if (exitCode === null || exitCode === undefined || exitCode === 0) {
    return text;
  }
  return text.replace(new RegExp(`\\n?Exit code: ${exitCode}\\s*$`), '');
}

// Per-tool-call streaming buffer (keyed by instance, managed externally)
const streamBuffers = new WeakMap<object, StreamingBuffer>();

function getOrCreateBuffer(key: object): StreamingBuffer {
  let buf = streamBuffers.get(key);
  if (!buf) {
    buf = new StreamingBuffer();
    streamBuffers.set(key, buf);
  }
  return buf;
}

/** Extract the first line of a command for display (heredocs/multi-line commands must not leak newlines into headers). */
function commandHeader(command: string, maxLen = 77): string {
  const firstLine = command.split('\n')[0] ?? command;
  const display = firstLine.length > maxLen ? firstLine.slice(0, maxLen - 3) + '...' : firstLine;
  return `Ran ${display}`;
}

const bashRenderer: ToolRenderer = {
  renderCall(args: Record<string, unknown>, _context: ToolRenderContext): ToolCallDisplay {
    return {
      headerText: commandHeader(String(args['command'] ?? '')),
      contentLines: [],
      footerText: '',
    };
  },

  renderResult(result: unknown, details: unknown, context: ToolRenderContext): ToolResultDisplay {
    const d = details as BashDetails | undefined;
    const header = commandHeader(String(context.args['command'] ?? 'command'));
    const isError = d ? (d.exitCode !== null && d.exitCode !== 0) : false;
    const timedOut = d?.timedOut ?? false;

    // Success is the common case: the command ran, no body needed. Output only
    // surfaces when something failed, so the user sees why without expanding.
    if (!isError && !timedOut && !context.expanded) {
      return { headerText: header, contentLines: [], footerText: '' };
    }

    const rawText = extractTextContent(result);
    const text = stripTrailingExitCode(rawText, d?.exitCode) || '(no output)';
    const allLines = text.split('\n').filter(l => l.length > 0 || text.includes('\n'));

    // Errors live at the end of output: show the tail.
    const { lines } = collapseContent(allLines, {
      mode: 'tail',
      limit: ERROR_HEAD + ERROR_TAIL,
      expanded: context.expanded,
    });

    const footerText = timedOut
      ? 'timed out'
      : (d?.exitCode !== null && d?.exitCode !== undefined && d.exitCode !== 0)
        ? `exit ${d.exitCode}`
        : '';

    return {
      headerText: header,
      contentLines: lines,
      footerText,
    };
  },

  renderStreamUpdate(update: unknown, context: ToolRenderContext): ToolResultDisplay {
    const u = update as { details?: BashStreamUpdate; content?: Array<{ text?: string }> } | undefined;
    const stdout = u?.details?.stdout ?? '';
    const totalLines = u?.details?.totalLines ?? 0;

    // Use args object as stable WeakMap key (same reference across all updates for one tool call)
    const buffer = getOrCreateBuffer(context.args);
    if (stdout) {
      buffer.append(stdout);
    }

    const visibleLines = buffer.getLines(STREAMING_WINDOW);

    return {
      headerText: commandHeader(String(context.args['command'] ?? 'command')),
      contentLines: visibleLines,
      footerText: `${totalLines} lines`,
    };
  },

  renderError(error: string, args: Record<string, unknown>, context: ToolRenderContext): ToolResultDisplay {
    const errorLines = error.split('\n').map(line => chalk.hex(context.theme.error)(line));

    const { lines } = collapseContent(errorLines, {
      mode: 'head',
      limit: ERROR_HEAD + ERROR_TAIL,
      expanded: context.expanded,
    });

    return {
      headerText: commandHeader(String(args['command'] ?? 'command')),
      contentLines: lines,
      footerText: 'failed',
    };
  },
};

registerRenderer('Bash', bashRenderer);
export { bashRenderer };
